import { useIsVitrine } from '@/lib/use-instance';
import { Link, useRouterState } from '@tanstack/react-router';

/**
 * The tab bar of Réglages, back after the coupe (§11.5).
 *
 * The coupe (issue #32) removed the bar and the `h1` TOGETHER, because a lone
 * tab is not a navigation: it leads nowhere else and it suggests a sibling that
 * does not exist. A second tab inverts the gesture exactly, which is why this
 * file and the `h1` of `reglages.tsx` land in the same lot and neither makes
 * sense alone.
 *
 * ## Its own list, and not `admin-nav.ts`
 *
 * `ADMIN_RAIL` is the eight destinations an operator learns once, and Réglages
 * is ONE of them. Its sub-tabs are the inside of that destination, so putting
 * them in the same catalogue would make `activeRailId` and `doorForPath` answer
 * for two different kinds of thing. Two short lists that never overlap beat one
 * list with a `kind` field nobody reads.
 *
 * ## ONE class string chosen by a ternary, and NOT `activeProps.className`
 *
 * The rule `admin-rail.tsx` states at length, and which that file has had to
 * relearn twice. TanStack CONCATENATES `className` and `activeProps.className`,
 * so an active anchor carries the base utility AND its override at EQUAL
 * specificity, in one attribute: the winner is decided by their order in the
 * generated stylesheet, not by their order in the string. Both times it went
 * green through the unit tests and the e2e assertions and was caught on the MR
 * screenshots.
 *
 * `className` is typed `string` on this version of `Link`, so the state comes
 * from `useRouterState` and the ternary picks ONE of two mutually exclusive
 * strings: whatever one sets, the other overrides. The comparison is EXACT and
 * not a prefix, which is the rail's rule too.
 *
 * `Link` still sets `aria-current="page"` itself, by prefix, and that is safe
 * here and only here because no tab's path is a prefix of another's -
 * `/admin/reglages/modeles`, `/admin/reglages/personas`, `/admin/reglages/partage`,
 * `/admin/reglages/regles`, `/admin/reglages/jetons`, `/admin/reglages/noyaux`,
 * `/admin/reglages/methode` and `/admin/reglages/rejeu` are siblings, and issues
 * #59, #62, #84, #148, #196 and #193 each kept that property when they added the
 * third, the fourth, the fifth, the sixth, the seventh and the one that exists
 * only where the replay is open. « noyaux » shares no prefix with the five before
 * it, which is what let it in without the rail's `activeRailId` machinery, « methode »
 * shares none with the six, and « rejeu » shares none with the seven; a NEXT one
 * has to keep the property too, or take the mark over as the rail does. « Personas »
 * and « Partage » share four letters and no path; so do « Règles » and
 * « Réglages », which is the shell above them and not a tab at all, and « Modèles »
 * and « Méthode », whose paths differ at the third letter.
 */

interface ReglagesTab {
  readonly to:
    | '/admin/reglages/modeles'
    | '/admin/reglages/personas'
    | '/admin/reglages/partage'
    | '/admin/reglages/regles'
    | '/admin/reglages/jetons'
    | '/admin/reglages/noyaux'
    | '/admin/reglages/methode'
    | '/admin/reglages/rejeu';
  readonly label: string;
}

/**
 * The seven tabs of every server, in reading order. Modèles first: it is the
 * older setting.
 * Partage third, and that is §11.4 in as many words - « troisième onglet après
 * Modèles et Personas ». Règles fourth, added by issue #62, and Jetons fifth,
 * added by issue #84: neither §11.3 nor §20.2 gives its tab a rank, and the end
 * of the bar is where a tab lands when nothing says otherwise - moving the ones
 * that exist would move an entry an operator has already learnt. Noyaux sixth,
 * added by issue #148, and this one IS ranked: « un sixième onglet, après
 * Modèles, Personas, Partage, Règles et Jetons » (noyau.md §6.3, N22). Méthode
 * seventh, added by issue #196 and ranked too: « un onglet Méthode, après
 * Noyaux » (analyste.md §4.4, K12) - which puts it before Rejeu, whose own rank
 * is « après Noyaux » and which is appended below, where the replay is open.
 */
export const REGLAGES_TABS: readonly ReglagesTab[] = [
  { to: '/admin/reglages/modeles', label: 'Modèles' },
  { to: '/admin/reglages/personas', label: 'Personas' },
  { to: '/admin/reglages/partage', label: 'Partage' },
  { to: '/admin/reglages/regles', label: 'Règles' },
  { to: '/admin/reglages/jetons', label: 'Jetons' },
  { to: '/admin/reglages/noyaux', label: 'Noyaux' },
  { to: '/admin/reglages/methode', label: 'Méthode' },
];

/**
 * The tab added by issue #193 (docs/specs/rejeu.md §10.3): « un septième onglet
 * de Réglages, Rejeu, après Noyaux » - the last of the bar since Méthode (#196)
 * came in between. It is NOT in `REGLAGES_TABS` and that is the point: the seven
 * above are on every server, and this one exists only where the replay is open
 * (§4) - `ReglagesTabs` appends it when told so, and a closed server (the
 * production) never draws it.
 */
export const REPLAY_TAB: ReglagesTab = { to: '/admin/reglages/rejeu', label: 'Rejeu' };

/** Everything a tab wears whatever its state - and nothing either state sets. */
const TAB_BASE = 'inline-flex h-11 items-center border-b-2 px-4 font-semibold text-sm';
const TAB_OFF = 'border-transparent text-gray-600 hover:text-bleu-nuit';
const TAB_ON = 'border-bleu-nuit text-bleu-nuit';

export interface ReglagesTabsProps {
  /**
   * The replay is open on this server (`GET /api/replay-settings` did not
   * answer 404): the tab of the replay is drawn, last. Absent or false, the bar is
   * the seven of every server - which is also what the tests that mount the bar
   * bare see.
   */
  readonly replayOpen?: boolean;
}

/**
 * The two tabs the vitrine does not have (docs/specs/vitrine.md section 6, issue
 * #192): Personas - the library of tones and levels, which only serves to WRITE
 * a persona (H1) - and Jetons - without the assistant a personal token serves
 * nothing (H6). Rejeu is the atelier's alone and never drawn there. Méthode (#196)
 * IS drawn: it reads there without a gesture, like Modèles, Règles and Noyaux
 * (vitrine.md section 7).
 */
const VITRINE_WITHOUT: readonly ReglagesTab['to'][] = [
  '/admin/reglages/personas',
  '/admin/reglages/jetons',
];

export function ReglagesTabs({ replayOpen = false }: ReglagesTabsProps = {}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const vitrine = useIsVitrine();
  const all = replayOpen && !vitrine ? [...REGLAGES_TABS, REPLAY_TAB] : REGLAGES_TABS;
  const tabs = vitrine ? all.filter((tab) => !VITRINE_WITHOUT.includes(tab.to)) : all;
  return (
    <nav aria-label="Réglages" data-testid="reglages-onglets" className="border-gray-200 border-b">
      <ul className="-mb-px flex flex-wrap gap-1">
        {tabs.map((tab) => (
          <li key={tab.to}>
            <Link to={tab.to} className={`${TAB_BASE} ${pathname === tab.to ? TAB_ON : TAB_OFF}`}>
              {tab.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
